import React, { useEffect, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { FaFileExcel, FaSpinner } from 'react-icons/fa';
import toast from 'react-hot-toast';
import axiosInstance from '../../../libs/axios';
import { Pagination, PaginationType } from '../../../utils/pagination.utils';
import { ExcelDownload } from '../../../utils/excel.utils';
import { dateRangeError, dateRangeInputBounds } from '../../../utils/date-range.utils';
import { formatDate } from '../../activities/edit';

interface GeneralClaimRow {
  id: number;
  staff: { id: number; name: string; employee_code: string };
  claim_date: string;
  category: string;
  description: string;
  status: string;
  amounts: { submitted: any; approved: any };
  [key: string]: any;
}

interface NormalizedPagination {
  total_items: number;
  current_page: number;
  total_pages: number;
  items_per_page: number;
  has_next_page: boolean;
  has_previous_page: boolean;
}

const GeneralClaimReport: React.FC = () => {
  const [claims, setClaims] = useState<GeneralClaimRow[]>([]);
  const [pagination, setPagination] = useState<NormalizedPagination>({ total_items: 0, current_page: 1, total_pages: 1, items_per_page: 10, has_next_page: false, has_previous_page: false });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [hasSearched, setHasSearched] = useState(false);
  const [showFilterForm, setShowFilterForm] = useState(true);
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [exportData, setExportData] = useState<any[]>([]);
  const [exporting, setExporting] = useState(false);

  const formatDateValue = (value?: string | null, fallback = '-') => (value ? formatDate(value) : fallback);

  const buildParams = (targetPage: number, targetLimit: number) => {
    const params: any = { page: targetPage, limit: targetLimit };
    if (search.trim()) params.search = search.trim();
    if (status) params.status = status;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    return params;
  };

  const fetchClaims = async (targetPage: number = page, targetLimit: number = pagination.items_per_page) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/reports/claims/general', { params: buildParams(targetPage, targetLimit) });
      if (response.data.success) {
        const payload = response.data.data || { claims: [] };
        const rows = payload.claims || [];
        setClaims(rows);
        const p = payload.pagination || {};
        const total = Number(p.total_items ?? p.total_count) || rows.length;
        const pages = Number(p.total_pages) || Math.max(1, Math.ceil(total / targetLimit));
        setPagination({
          total_items: total,
          current_page: targetPage,
          total_pages: pages,
          items_per_page: targetLimit,
          has_next_page: targetPage < pages,
          has_previous_page: targetPage > 1
        });
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch general claim reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasSearched) return;
    fetchClaims(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, hasSearched]);

  const mapClaimForExport = (c: GeneralClaimRow) => ({
    ID: c.id,
    Staff: c.staff?.name || '',
    'Employee Code': c.staff?.employee_code || '',
    'Claim Date': c.claim_date ? formatDate(c.claim_date) : '',
    Category: c.category || '',
    Description: c.description || '',
    Status: c.status || '',
    'Submitted Amount': typeof c.amounts?.submitted === 'object' ? JSON.stringify(c.amounts.submitted) : c.amounts?.submitted ?? '',
    'Approved Amount': typeof c.amounts?.approved === 'object' ? JSON.stringify(c.amounts.approved) : c.amounts?.approved ?? '',
  });

  const fetchExportData = async () => {
    // The filter form can be edited after a search, so the export checks it too.
    const dateError = dateRangeError(dateFrom, dateTo, 'Date range');
    if (dateError) {
      toast.error(dateError);
      return;
    }
    try {
      setExporting(true);
      let currentPage = 1;
      let totalPages = 1;
      const pageSize = 100;
      const allClaims: GeneralClaimRow[] = [];

      do {
        const response = await axiosInstance.get('/reports/claims/general', {
          params: buildParams(currentPage, pageSize),
        });
        if (!response.data.success) break;
        const payload = response.data.data || { claims: [] };
        const rows: GeneralClaimRow[] = payload.claims || [];
        allClaims.push(...rows);
        const p = payload.pagination || {};
        totalPages =
          Number(p.total_pages) ||
          Math.max(1, Math.ceil((Number(p.total_items ?? p.total_count) || allClaims.length) / pageSize));
        currentPage += 1;
      } while (currentPage <= totalPages);

      setExportData(allClaims.map(mapClaimForExport));
      setShowExcelModal(true);
    } catch (error) {
      console.error(error);
      toast.error('Failed to prepare general claim export');
    } finally {
      setExporting(false);
    }
  };

  const handleApplyFilters = () => {
    const dateError = dateRangeError(dateFrom, dateTo, 'Date range');
    if (dateError) {
      toast.error(dateError);
      return;
    }
    setPage(1);
    setHasSearched(true);
    setShowFilterForm(false);
    fetchClaims(1, pagination.items_per_page);
  };

  const handleResetDraftFilters = () => {
    setSearch('');
    setStatus('');
    setDateFrom('');
    setDateTo('');
  };

  const handleClearFilters = () => {
    handleResetDraftFilters();
    setClaims([]);
    setHasSearched(false);
    setShowFilterForm(true);
    setPage(1);
    setPagination({ total_items: 0, current_page: 1, total_pages: 1, items_per_page: pagination.items_per_page, has_next_page: false, has_previous_page: false });
  };

  const paginationAdapter: PaginationType = {
    offset: Math.max((pagination.current_page - 1) * pagination.items_per_page, 0),
    limit: pagination.items_per_page,
    page: pagination.current_page,
    total_pages: pagination.total_pages,
    total_count: pagination.total_items,
    has_next_page: pagination.has_next_page,
    has_prev_page: pagination.has_previous_page,
    next_page: pagination.has_next_page ? pagination.current_page + 1 : null,
    prev_page: pagination.has_previous_page ? pagination.current_page - 1 : null
  };

  const getStatusBadge = (s: string) => {
    const map: Record<string, string> = {
      submitted: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      pending: 'bg-blue-100 text-blue-700',
    };
    return map[s] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">General Claim Report</h1>
            <p className="text-sm text-gray-600 mt-1">View general expense claim reports.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {!showFilterForm && (
              <button
                onClick={() => setShowFilterForm(true)}
                className="px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
              >
                Apply Filters Again
              </button>
            )}
            <button
              onClick={handleClearFilters}
              className="px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
            >
              Clear
            </button>
            <button
              onClick={fetchExportData}
              disabled={!hasSearched || exporting}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400"
            >
              {exporting ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FaFileExcel className="w-4 h-4" />}
              {exporting ? 'Preparing...' : 'Export'}
            </button>
          </div>
        </div>
      </div>

      {showFilterForm ? (
        <div className="bg-white shadow-sm rounded border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            <div className="relative">
              <FiSearch className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
            </div>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-2 border border-gray-300 rounded text-sm">
              <option value="">All Status</option>
              <option value="submitted">Submitted</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="pending">Pending</option>
            </select>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} {...dateRangeInputBounds(dateFrom, dateTo).from} className="px-3 py-2 border border-gray-300 rounded text-sm" />
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} {...dateRangeInputBounds(dateFrom, dateTo).to} className="px-3 py-2 border border-gray-300 rounded text-sm" />
            <div className="flex items-center gap-2">
              <button onClick={handleApplyFilters} className="px-3 py-2 text-sm bg-blue-800 text-white rounded hover:bg-blue-900">Apply Filters</button>
              <button onClick={handleResetDraftFilters} className="px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50">Reset</button>
            </div>
          </div>
        </div>
      ) : (
      <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Staff</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Claim Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Category</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Description</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Submitted Amt</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Approved Amt</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-600">Loading...</td></tr> :
                claims.length === 0 ? <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-600">No claims found.</td></tr> :
                  claims.map((c) => (
                    <tr key={c.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {c.staff?.name || '-'}
                        <div className="text-xs text-gray-500">{c.staff?.employee_code || ''}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{formatDateValue(c.claim_date)}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{c.category || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate">{c.description || '-'}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`inline-flex px-2 py-1 rounded text-xs font-medium capitalize ${getStatusBadge(c.status)}`}>{c.status}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 font-medium">{typeof c.amounts?.submitted === 'object' ? JSON.stringify(c.amounts.submitted) : c.amounts?.submitted ?? '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 font-medium">{typeof c.amounts?.approved === 'object' ? JSON.stringify(c.amounts.approved) : c.amounts?.approved ?? '-'}</td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
        <Pagination
          pagination={paginationAdapter}
          onPageChange={(nextPage) => {
            if (!hasSearched) return;
            setPage(nextPage);
          }}
          onLimitChange={(nextLimit) => {
            setPagination(prev => ({ ...prev, items_per_page: nextLimit }));
            setPage(1);
            if (!hasSearched) return;
            fetchClaims(1, nextLimit);
          }}
        />
      </div>
      )}
      <ExcelDownload
        isOpen={showExcelModal}
        onClose={() => setShowExcelModal(false)}
        data={exportData}
        filename="general_claim_reports"
        title="Export General Claim Reports"
        description="Download general claim reports with current filters"
      />
    </div>
  );
};

export default GeneralClaimReport;
